import React, { useContext } from "react"
import { Box, Dialog, Progress, Stepper } from "@carrotfertility/carotene-core"
import { DialogCloseButton } from "@carrotfertility/carotene-core-x"
import { StateMachineProvider, StateMachineStepView, useStateMachine } from "../context/stateMachine/StateMachineV2"
import { useTextDirection } from "#/services/locale/hooks/useTextDirection"
import { carrotCardCostShareSteps, carrotCardCostShareWorkflows, Workflows } from "./workflows/workflow"
import { fertilitySteps, fertilityWorkflows, Workflows as FertilityWorkflows } from "./workflows/fertilityWorkflow"
import {
  gestationalSteps,
  gestationalWorkflows,
  Workflows as GestationalSurrogacyWorkflows
} from "./workflows/gestationalWorkflow"
import {
  preservationSteps,
  preservationWorkflows,
  Workflows as PreservationWorkflows
} from "#/components/carrot-card-cost-share/workflows/preservationWorkflow"
import { useIntl } from "react-intl"
import {
  CarrotCardCostSharePreAuthContext,
  CarrotCardCostShareProvider
} from "./state/CarrotCardCostSharePreAuthContext"
import useGetBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import {
  AdoptionSteps,
  FertilitySteps,
  GeneralSteps,
  PreservationSteps,
  GestationalSteps,
  PregnantPostpartumSteps,
  MetabolicSteps,
  GenderAffirmingCareSteps,
  MenopauseSteps,
  LowTestosteroneSteps
} from "#/components/carrot-card-cost-share/workflows/step-library"
import {
  adoptionSteps,
  adoptionWorkflows,
  Workflows as AdoptionWorkflows
} from "#/components/carrot-card-cost-share/workflows/adoptionWorkflow"
import {
  pregnantPostpartumSteps,
  pregnantPostpartumWorkflows,
  Workflows as PregnantPostpartumWorkflows
} from "#/components/carrot-card-cost-share/workflows/pregnantPostpartumWorkflow"
import {
  metabolicSteps,
  metabolicWorkflows,
  Workflows as MetabolicWorkflows
} from "#/components/carrot-card-cost-share/workflows/metabolicWorkflow"
import {
  genderAffirmingCareSteps,
  genderAffirmingCareWorkflows,
  Workflows as GenderAffirmingCareWorkflows
} from "#/components/carrot-card-cost-share/workflows/genderAffirmingCareWorkflow"
import {
  menopauseSteps,
  menopauseWorkflows,
  Workflows as MenopauseWorkflows
} from "#/components/carrot-card-cost-share/workflows/menopauseWorkflow"
import {
  lowTestosteroneSteps,
  lowTestosteroneWorkflows,
  Workflows as LowTestosteroneWorkflows
} from "#/components/carrot-card-cost-share/workflows/lowTestosteroneWorkflow"
import {
  getActiveStepForSection,
  getTotalStepsForSection,
  isExcludedFromProgress
} from "#/components/carrot-card-cost-share/steps/shared/progressHelpers"
import { useWhoWillReceiveCareEligibility } from "#/components/carrot-card-cost-share/state/useWhoWillRecieveCareEligibility"
import { useIvfRequirementsInfo } from "#/components/views/reimbursements/hooks/useIvfRequirementsInfo"

enum Sections {
  GENERAL = "General",
  FERTILITY = "Fertility",
  ADOPTION = "Adoption",
  GESTATIONAL = "Gestational",
  PRESERVATION = "Preservation",
  PREGNANT_AND_POSTPARTUM = "Pregnant And Postpartum",
  METABOLIC = "Metabolic",
  GENDER_AFFIRMING_CARE = "Gender Affirming Care",
  MENOPAUSE = "Menopause",
  LOW_TESTOSTERONE = "Low Testosterone"
}

interface CarrotCardCostSharePreAuthProps {
  open: boolean
  onClose: () => void
}

function CarrotCardCostSharePreAuthFlow({ open, onClose }: CarrotCardCostSharePreAuthProps): JSX.Element {
  const intl = useIntl()
  const { dispatchFormState } = useContext(CarrotCardCostSharePreAuthContext)
  const carrotCardCostShareSection = {
    name: Sections.GENERAL,
    initialStep: GeneralSteps.CANCEL_EXISTING_AUTH,
    desiredWorkflow: Workflows.CARROT_CARD_COST_SHARE,
    steps: carrotCardCostShareSteps,
    workflows: carrotCardCostShareWorkflows
  }

  const fertilitySection = {
    name: Sections.FERTILITY,
    initialStep: FertilitySteps.RELATED_TO_STORAGE_FEES,
    desiredWorkflow: FertilityWorkflows.FERTILITY,
    steps: fertilitySteps,
    workflows: fertilityWorkflows
  }

  const adoptionSection = {
    name: Sections.ADOPTION,
    initialStep: AdoptionSteps.WHO_WILL_RECEIVE_CARE_ADOPTION,
    desiredWorkflow: AdoptionWorkflows.ADOPTION,
    steps: adoptionSteps,
    workflows: adoptionWorkflows
  }

  const gestationalCarrierSection = {
    name: Sections.GESTATIONAL,
    initialStep: GestationalSteps.WHO_WILL_RECEIVE_CARE_GC,
    desiredWorkflow: GestationalSurrogacyWorkflows.GESTATIONAL,
    steps: gestationalSteps,
    workflows: gestationalWorkflows
  }

  const pregnantPostpartumSection = {
    name: Sections.PREGNANT_AND_POSTPARTUM,
    initialStep: PregnantPostpartumSteps.CARROT_CARD_CANT_BE_USED,
    desiredWorkflow: PregnantPostpartumWorkflows.PREGNANT_AND_POSTPARTUM,
    steps: pregnantPostpartumSteps,
    workflows: pregnantPostpartumWorkflows
  }

  const preservationSection = {
    name: Sections.PRESERVATION,
    initialStep: PreservationSteps.RELATED_TO_STORAGE_FEES_PRESERVATION,
    desiredWorkflow: PreservationWorkflows.PRESERVATION,
    steps: preservationSteps,
    workflows: preservationWorkflows
  }

  const metabolicSection = {
    name: Sections.METABOLIC,
    initialStep: MetabolicSteps.WHO_WILL_RECEIVE_CARE_METABOLIC,
    desiredWorkflow: MetabolicWorkflows.METABOLIC,
    steps: metabolicSteps,
    workflows: metabolicWorkflows
  }

  const genderAffirmingCareSection = {
    name: Sections.GENDER_AFFIRMING_CARE,
    initialStep: GenderAffirmingCareSteps.WHO_WILL_RECEIVE_CARE_GAC,
    desiredWorkflow: GenderAffirmingCareWorkflows.GENDER_AFFIRMING_CARE,
    steps: genderAffirmingCareSteps,
    workflows: genderAffirmingCareWorkflows
  }

  const menopauseSection = {
    name: Sections.MENOPAUSE,
    initialStep: MenopauseSteps.WHO_WILL_RECEIVE_CARE_MENOPAUSE,
    desiredWorkflow: MenopauseWorkflows.MENOPAUSE,
    steps: menopauseSteps,
    workflows: menopauseWorkflows
  }

  const lowTestosteroneSection = {
    name: Sections.LOW_TESTOSTERONE,
    initialStep: LowTestosteroneSteps.WHO_WILL_RECEIVE_CARE_LOW_T,
    desiredWorkflow: LowTestosteroneWorkflows.LOW_TESTOSTERONE,
    steps: lowTestosteroneSteps,
    workflows: lowTestosteroneWorkflows
  }

  const handleComplete = () => {
    onClose()
    dispatchFormState({ type: "RESET_STATE" })
  }
  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <StateMachineProvider
        initialSection={Sections.GENERAL}
        stateMachineDescription={[
          carrotCardCostShareSection,
          fertilitySection,
          adoptionSection,
          gestationalCarrierSection,
          preservationSection,
          pregnantPostpartumSection,
          metabolicSection,
          genderAffirmingCareSection,
          menopauseSection,
          lowTestosteroneSection
        ]}
        onComplete={handleComplete}
      >
        <DialogCloseButton aria-label={intl.formatMessage({ defaultMessage: "Close" })} onClick={handleComplete} />
        <ProgressBarAndContent />
        <StateMachineStepView />
      </StateMachineProvider>
    </Dialog>
  )
}

function ProgressBarAndContent(): JSX.Element {
  const { sectionName, stepName } = useStateMachine()
  const textDirection = useTextDirection()

  return (
    !isExcludedFromProgress(stepName) && (
      <Stepper
        dir={textDirection}
        sx={{ marginBlockEnd: (theme) => theme.tokens.spacing.lg }}
        steps={getTotalStepsForSection(sectionName)}
        position="static"
        activeStep={getActiveStepForSection(sectionName, stepName)}
      />
    )
  )
}

export function CarrotCardCostSharePreAuth({ open, onClose }: CarrotCardCostSharePreAuthProps): JSX.Element {
  const benefit = useGetBenefit()
  const partnerEligibilityQuery = useWhoWillReceiveCareEligibility()
  const ivfRequirementsInfo = useIvfRequirementsInfo()

  if (!open) return null

  const serverState = {
    hasFertilityCareCoverage: benefit?.data?.journeys?.fertilityCare?.hasEligibleExpenses,
    hasFerilityPreservationCoverage: benefit?.data?.journeys?.fertilityPreservation?.hasEligibleExpenses,
    hasDonorAssistanceCoverage: benefit?.data?.journeys?.fertilityCare?.eligibleExpensesDetail?.hasDonorAssistance,
    hasGestationalCarrierCoverage: benefit?.data?.journeys?.gestationalCarrier?.hasEligibleExpenses,
    isCarrotRxRequired: benefit?.data?.carrotRx?.isCarrotRxRequired,
    hasFertilityCareRxCoverage: benefit?.data?.journeys?.fertilityCare?.eligibleExpensesDetail?.isRxCovered,
    hasElectiveFertilityCareCoverage:
      benefit?.data?.journeys?.fertilityCare?.eligibleExpensesDetail?.isElectiveCareCovered,
    hasInfertilityDxCoverage:
      benefit?.data?.journeys?.fertilityCare?.eligibleExpensesDetail?.isCoveredWithInfertilityDiagnosis,
    hasElectivePreservationCoverage:
      benefit?.data?.journeys?.fertilityPreservation?.eligibleExpensesDetail.isElectivePreservationCovered,
    hasPreservationRxCoverage: benefit?.data?.journeys?.fertilityPreservation?.eligibleExpensesDetail.isRxCovered,
    isMedicallyNecessaryPreservationCovered:
      benefit?.data?.journeys?.fertilityPreservation?.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered,
    isGestationalCarrierRxCovered: benefit?.data?.journeys?.gestationalCarrier?.eligibleExpensesDetail?.isRxCovered,
    checkPartnerEligibility: partnerEligibilityQuery.checkPartnerEligibility,
    hasCompletedIvfRequirements: ivfRequirementsInfo.data?.hasCompletedIvfRequirements
  }

  return benefit.isLoading || partnerEligibilityQuery.isLoading || ivfRequirementsInfo.isLoading ? (
    <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
      <Progress />
    </Box>
  ) : (
    <CarrotCardCostShareProvider {...{ serverState }}>
      <CarrotCardCostSharePreAuthFlow {...{ open, onClose }} />
    </CarrotCardCostShareProvider>
  )
}
